import {
    Button,
    CircularProgress,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    makeStyles,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import React, { useEffect, useState } from "react";
import { streamNames } from "../../util/constants";
import { parseMessage } from "../../util/helperFunctions";
import { createStreamReader } from "../services/streamsService";

const useStyles = makeStyles({
    root: {
        paddingTop: "1.5rem",
    },
    filterForm: {},
    emptyCell: {
        borderBottom: "none",
    },
    emptyRow: {
        height: "10rem",
    },
    select: {
        width: "100%",
    },
    textField: {
        height: "auto",
    },
    actionButton: {
        backgroundColor: "rgb(208 225 243)",
        width: "100%",
        boxShadow: "none",
        border: "1px solid rgb(169,169,169)",
    },
    selectTitle: {
        paddingBottom: "0.5rem",
    },
    tableheading: {
        paddingBottom: "0.5rem",
    },
});

const FilteredSecurities = ({ plan, isSubscribed, setIsSubscribed, isStreamAppStarted, clearData }) => {
    const columns = [
        { id: "SummarizationItemId", label: "Summarization Item Id" },
        { id: "SecurityName", label: "Security Name" },
        {
            id: "PositiveSentimentVerified",
            label: "Positive Sentiment Verified",
        },
        {
            id: "NegativeSentimentVerified",
            label: "Negative Sentiment Verified",
        },
        {
            id: "NeutralSentimentVerified",
            label: "Neutral Sentiment Verified",
        },
    ];
    const filterOperations = [
        { id: "equals", label: "Equals to", operation: "==" },
        { id: "notEquals", label: "Not Equals to", operation: "!=" },
        { id: "greaterThan", label: "Greater than", operation: ">" },
        { id: "lessThan", label: "Less than", operation: "<" },
        { id: "greaterThanOrEquals", label: "Greater than or equal to", operation: ">=" },
        { id: "lessThanOrequals", label: "Less than or equal to", operation: "<=" },
    ];
    const classes = useStyles();

    const [isSubscribing, setIsSubscribing] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const [errorMessage, setErrorMessage] = useState("");
    const [conditionErrorMessage, setConditionErrorMessage] = useState("");
    const [filters, setFilters] = useState([]);
    const [filterCondition, setFilterCondition] = useState("");
    const [selectedFilterColumn, setSelectedFilterColumn] = useState("");
    const [selectedFilterOperation, setSelectedFilterOperation] = useState("");
    const [filterValue, setFilterValue] = useState("");

    const [filteredSecuritySentimentData, setFilteredSecuritySentimentData] = useState([]);
    const [streamConsumer, setStreamConsumer] = useState(null);

    useEffect(() => {
        if (!isStreamAppStarted && streamConsumer) {
            stopFilterdStream();
        }
    }, [isStreamAppStarted]);

    useEffect(() => {
        if (clearData) {
            setFilters([]);
            setFilteredSecuritySentimentData([]);
        }
    }, [clearData]);

    useEffect(() => {}, [filters.length]);

    const addFilter = () => {
        if (selectedFilterColumn && selectedFilterOperation && filterValue) {
            if (selectedFilterColumn !== columns[1].id && !Number.isInteger(Number(filterValue))) {
                setErrorMessage("Please enter number");
                return;
            }

            const _filterValue = selectedFilterColumn !== columns[1].id ? Number(filterValue) : filterValue;
            setFilters((prev) => {
                return [...prev, { key: selectedFilterColumn, value: _filterValue, op: selectedFilterOperation }];
            });
            setSelectedFilterColumn("");
            setFilterValue("");
            setSelectedFilterOperation("");
        }
    };

    const removeFilter = (index) => {
        filters.splice(index, 1);
        setFilters((prev) => [...filters]);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const messageManipulation = (message) => {
        let tempArr = [];
        const { newData } = parseMessage(message);
        if (!newData || !newData.SummarizationItemId) {
            return;
        }

        tempArr.push(newData);
        setFilteredSecuritySentimentData((prev) => [...tempArr, ...prev]);
    };

    const subscribeFilterdStream = async () => {
        if (filters.length > 1 && !filterCondition) {
            setConditionErrorMessage("Please select Condition");
            return;
        }

        setFilteredSecuritySentimentData([]);
        setIsSubscribing(true);

        try {
            const streamName = plan === "stock" ? streamNames[0] : streamNames[1];
            let _filters = {
                expressions: [],
                condition: filterCondition,
            };

            _filters.expressions = filters.reduce((prevFilters, filter) => {
                const _filter = {
                    key: filter.key,
                    value: filter.value,
                    op: filter.op.id,
                };
                prevFilters.push(_filter);
                return prevFilters;
            }, []);

            const _consumer = await createStreamReader(streamName, _filters);
            _consumer.on("open", () => console.log(`Connection open for ${streamName}`));
            _consumer.on("error", (error) => console.error(`Connection error for ${streamName}`, error));
            _consumer.on("close", () => console.log(`Connection close for ${streamName}`));

            _consumer.on("message", (message) => {
                _consumer.send(JSON.stringify({ messageId: JSON.parse(message).messageId }));

                messageManipulation(message);
            });

            setStreamConsumer(_consumer);
        } catch (error) {
            console.error("error", error);
        }

        setIsSubscribing(false);
        setIsSubscribed(true);
    };

    const stopFilterdStream = async () => {
        try {
            await streamConsumer.terminate();
        } catch (error) {
            console.error(error);
        }
        setIsSubscribed(false);
    };

    const emptyRows =
        filteredSecuritySentimentData &&
        filteredSecuritySentimentData.length &&
        rowsPerPage - Math.min(rowsPerPage, filteredSecuritySentimentData.length - page * rowsPerPage);

    return (
        <div className={classes.root}>
            <div>
                <Typography variant="subtitle2" className={classes.selectTitle}>
                    Stream Filters:
                </Typography>
                <form className={classes.filterForm}>
                    <Grid container alignItems="flex-start" spacing={2}>
                        <Grid item xs={3}>
                            <FormControl variant="outlined" className={classes.select}>
                                <InputLabel id="fieldSelect">Field</InputLabel>
                                <Select
                                    label="Field"
                                    labelId="fieldSelect"
                                    className={classes.select}
                                    value={selectedFilterColumn}
                                    onChange={(event) => setSelectedFilterColumn(event.target.value)}
                                >
                                    {columns.map((column, index) => {
                                        if (column.id === "SummarizationItemId") return;

                                        return (
                                            <MenuItem key={index} value={column.id}>
                                                {column.label}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={3}>
                            <FormControl variant="outlined" className={classes.select}>
                                <InputLabel id="expressionSelect">Expression</InputLabel>
                                <Select
                                    label="Expression"
                                    labelId="expressionSelect"
                                    className={classes.select}
                                    value={selectedFilterOperation.id || ""}
                                    onChange={(event) => {
                                        setSelectedFilterOperation(
                                            filterOperations.find((f) => f.id === event.target.value)
                                        );
                                    }}
                                >
                                    {filterOperations.map((filterOperation, index) => (
                                        <MenuItem key={index} value={filterOperation.id}>
                                            {filterOperation.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={2}>
                            <TextField
                                error={!!errorMessage}
                                className={classes.textField}
                                variant="outlined"
                                placeholder="Value"
                                helperText={errorMessage}
                                fullWidth
                                value={filterValue}
                                onChange={(event) => {
                                    setErrorMessage("");
                                    setFilterValue(event.target.value);
                                }}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <FormControl variant="outlined" className={classes.select} error={!!conditionErrorMessage}>
                                <InputLabel id="conditionSelect">Condition</InputLabel>
                                <Select
                                    label="Condition"
                                    labelId="conditionSelect"
                                    className={classes.select}
                                    value={filterCondition}
                                    onChange={(event) => {
                                        setConditionErrorMessage("");
                                        setFilterCondition(event.target.value);
                                    }}
                                >
                                    <MenuItem value="and">AND</MenuItem>
                                    <MenuItem value="or">OR</MenuItem>
                                </Select>
                                <FormHelperText>{conditionErrorMessage}</FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={2}>
                            <Button
                                variant="contained"
                                className={classes.actionButton}
                                onClick={addFilter}
                                disabled={!isStreamAppStarted || isSubscribed}
                            >
                                Add Filter
                            </Button>
                        </Grid>
                    </Grid>
                </form>
                {filters.length ? (
                    <Grid container alignItems="flex-end" spacing={2}>
                        <Grid item xs={8}>
                            <List dense>
                                {filters.map((filter, index) => {
                                    return (
                                        <ListItem key={index} button>
                                            <ListItemText
                                                primary={`${filter.key} ${filter.op.operation} ${filter.value}`}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    aria-label="delete"
                                                    disabled={isSubscribed}
                                                    onClick={() => removeFilter(index)}
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Grid>
                        <Grid item xs={2}>
                            <Button
                                variant="contained"
                                disabled={isSubscribed || !isStreamAppStarted}
                                className={classes.actionButton}
                                onClick={subscribeFilterdStream}
                            >
                                {isSubscribing ? <CircularProgress size={24} /> : "Subscribe"}
                            </Button>
                        </Grid>
                        <Grid item xs={2}>
                            <Button
                                variant="contained"
                                className={classes.actionButton}
                                disabled={!isSubscribed}
                                onClick={stopFilterdStream}
                            >
                                Stop
                            </Button>
                        </Grid>
                    </Grid>
                ) : (
                    <></>
                )}
            </div>
            <div>
                <Typography variant="subtitle2" className={classes.tableheading}>
                    Filtered Alerts:
                </Typography>
                <Paper>
                    <TableContainer className={classes.container}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell key={column.id} align="center">
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredSecuritySentimentData && filteredSecuritySentimentData.length ? (
                                    filteredSecuritySentimentData
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((security, index) => {
                                            return (
                                                <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                                                    <TableCell align="center">{security.SummarizationItemId}</TableCell>
                                                    <TableCell align="center">{security.SecurityName}</TableCell>
                                                    <TableCell align="center">
                                                        {security.PositiveSentimentVerified}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {security.NegativeSentimentVerified}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {security.NeutralSentimentVerified}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                ) : (
                                    <TableRow className={classes.emptyRow}>
                                        <TableCell className={classes.emptyCell} />
                                        <TableCell className={classes.emptyCell} />
                                        <TableCell align="center" component="th" className={classes.emptyCell}>
                                            {!isSubscribed ? <></> : <CircularProgress size={30} />}
                                        </TableCell>
                                        <TableCell className={classes.emptyCell} />
                                        <TableCell className={classes.emptyCell} />
                                    </TableRow>
                                )}
                                {emptyRows > 0 && (
                                    <TableRow style={{ height: 53 * emptyRows }}>
                                        <TableCell colSpan={6} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5]}
                        component="div"
                        count={filteredSecuritySentimentData && filteredSecuritySentimentData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </Paper>
            </div>
        </div>
    );
};

export default FilteredSecurities;
